import type { CharacterState } from "../data/types";
import { LEGACY_RULES_VERSION } from "../data/rules";

export function encodeCharacter(state: CharacterState): string {
  const payload = {
    v: state.rulesVersion,
    n: state.name,
    bg: state.background,
    sk: state.coreSkillSelections.map((s) => [s.skillId, s.count]),
    p: state.pacts.map((p) => [
      p.darkOfferingId,
      p.patronId,
      p.giftId,
      p.giftSkillId || "",
      p.choiceSwapFromId || 0,
      p.choiceSwapToId || 0,
    ]),
    sp: state.spellAllocations.map((a) => [a.spellId, a.points]),
    bsp: state.bonusSkillPoints,
    bgifts: state.bonusGifts,
    bp: (state.bonusPacts || []).map((p) => [
      p.darkOfferingId,
      p.patronId,
      p.giftId,
      p.giftSkillId || "",
      p.choiceSwapFromId || 0,
      p.choiceSwapToId || 0,
    ]),
    so: state.standaloneOfferings || [],
    wo: state.wonBackOfferings || [],
    sg: (state.standaloneGifts || []).map((g) => [
      g.giftId,
      g.giftSkillId || "",
      g.patronId || "",
      g.choiceSwapFromId || 0,
      g.choiceSwapToId || 0,
      g.exchangeOfferingId || 0,
      g.source || "",
    ]),
    rp: (state.resurrectionPacts || []).map((rp) => [
      rp.darkOfferingId,
      rp.patronId,
    ]),
    bs: state.bloodSacrifices || 0,
    uw: state.ultimateWarlock ? 1 : 0,
  };
  return btoa(JSON.stringify(payload));
}

export function decodeCharacter(encoded: string): CharacterState | null {
  try {
    const payload = JSON.parse(atob(encoded));
    return {
      rulesVersion: payload.v || LEGACY_RULES_VERSION,
      name: payload.n || "",
      background: payload.bg || "",
      coreSkillSelections: (payload.sk || []).map(
        ([skillId, count]: [string, number]) => ({ skillId, count })
      ),
      pacts: (payload.p || []).map(
        ([
          darkOfferingId,
          patronId,
          giftId,
          giftSkillId,
          choiceSwapFromId,
          choiceSwapToId,
        ]: [number, string, string, string, number, number]) => ({
          darkOfferingId,
          patronId,
          giftId,
          giftSkillId: giftSkillId || undefined,
          choiceSwapFromId: choiceSwapFromId || undefined,
          choiceSwapToId: choiceSwapToId || undefined,
        })
      ),
      spellAllocations: (payload.sp || []).map(
        ([spellId, points]: [string, number]) => ({ spellId, points })
      ),
      bonusSkillPoints: payload.bsp || 0,
      bonusGifts: payload.bgifts || 0,
      bonusPacts: (payload.bp || []).map(
        ([
          darkOfferingId,
          patronId,
          giftId,
          giftSkillId,
          choiceSwapFromId,
          choiceSwapToId,
        ]: [number, string, string, string, number, number]) => ({
          darkOfferingId,
          patronId,
          giftId,
          giftSkillId: giftSkillId || undefined,
          choiceSwapFromId: choiceSwapFromId || undefined,
          choiceSwapToId: choiceSwapToId || undefined,
        })
      ),
      standaloneOfferings: payload.so || undefined,
      wonBackOfferings: payload.wo || undefined,
      standaloneGifts:
        payload.so !== undefined
          ? (payload.sg || []).map(
              ([
                giftId,
                giftSkillId,
                patronId,
                choiceSwapFromId,
                choiceSwapToId,
                exchangeOfferingId,
                source,
              ]: [string, string, string, number, number, number, string]) => ({
                giftId,
                giftSkillId: giftSkillId || undefined,
                patronId: patronId || undefined,
                choiceSwapFromId: choiceSwapFromId || undefined,
                choiceSwapToId: choiceSwapToId || undefined,
                exchangeOfferingId: exchangeOfferingId || undefined,
                source: (source as "mission" | "exchange") || undefined,
              })
            )
          : undefined,
      resurrectionPacts: payload.rp?.length
        ? (payload.rp as [number, string][]).map(
            ([darkOfferingId, patronId]) => ({
              darkOfferingId,
              patronId,
            })
          )
        : undefined,
      bloodSacrifices: payload.bs || undefined,
      ultimateWarlock: payload.uw ? true : undefined,
    };
  } catch {
    return null;
  }
}

export function buildShareUrl(state: CharacterState): string {
  const encoded = encodeCharacter(state);
  const url = new URL(window.location.href);
  url.pathname = "/creator";
  url.searchParams.set("c", encoded);
  return url.toString();
}
